import React from 'react'
import { labelAnchor } from './geometry'

/**
 * The image with an SVG overlay in percentage space (viewBox 0 0 100 100), shared by the
 * student question, the instructor viewer, and the editor. Pins are HTML so they stay round
 * however the image is scaled.
 */

const STAGE = { position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0 }
const IMAGE = { display: 'block', maxWidth: '100%', height: 'auto', userSelect: 'none' }
const OVERLAY = { position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }

export const REGION_STYLES = {
  correct: { stroke: 'var(--success)', fill: 'var(--success-tint)', fillOpacity: 0.45, strokeWidth: 3 },
  distractor: { stroke: 'var(--nontext)', fill: 'var(--neutral-tint)', fillOpacity: 0.35, strokeWidth: 2, strokeDasharray: '6 4' },
  editing: { stroke: 'var(--brand)', fill: 'var(--brand-tint)', fillOpacity: 0.35, strokeWidth: 2 },
  selected: { stroke: 'var(--focus)', fill: 'var(--brand-tint)', fillOpacity: 0.55, strokeWidth: 3 },
}

const PIN_COLORS = {
  default: 'var(--brand)',
  correct: 'var(--success)',
  incorrect: 'var(--danger)',
}

const LABEL = {
  position: 'absolute', lineHeight: 1.2,
  fontSize: '0.75rem', fontWeight: 600, padding: '0.1rem 0.35rem', borderRadius: '3px',
  backgroundColor: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-bold)',
  pointerEvents: 'none', whiteSpace: 'nowrap', fontFamily: 'var(--me-font-ui)',
}

export const RegionShape = ({ region, style }) => {
  const c = region.coords || []
  const common = { ...style, vectorEffect: 'non-scaling-stroke' }
  if (region.shape === 'rect' && c.length === 4) {
    return <rect x={c[0]} y={c[1]} width={c[2]} height={c[3]} {...common} />
  }
  if (region.shape === 'ellipse' && c.length === 4) {
    return <ellipse cx={c[0]} cy={c[1]} rx={c[2]} ry={c[3]} {...common} />
  }
  if (region.shape === 'polygon' && c.length >= 4) {
    const points = []
    for (let i = 0; i + 1 < c.length; i += 2) points.push(`${c[i]},${c[i + 1]}`)
    const Tag = c.length >= 6 ? 'polygon' : 'polyline'
    return <Tag points={points.join(' ')} {...common} />
  }
  return null
}

export const RegionLabel = ({ region }) => {
  const { x, y, below } = labelAnchor(region)
  const transform = below ? 'translate(-50%, 4px)' : 'translate(-50%, calc(-100% - 4px))'
  return <span style={{ ...LABEL, left: `${x}%`, top: `${y}%`, transform }}>{region.label}</span>
}

export const Pin = ({ pin, number, status = 'default', onActivate, label }) => {
  const style = {
    position: 'absolute', left: `${pin.x}%`, top: `${pin.y}%`,
    width: 26, height: 26, marginLeft: -13, marginTop: -13, padding: 0,
    borderRadius: '50%', border: '2px solid var(--surface)',
    backgroundColor: PIN_COLORS[status] || PIN_COLORS.default, color: 'var(--text-on-brand)',
    fontSize: '0.75rem', fontWeight: 700, lineHeight: '22px', textAlign: 'center',
    boxShadow: '0 1px 3px var(--shadow)', cursor: onActivate ? 'pointer' : 'default',
  }
  if (!onActivate) {
    return <span style={style} aria-hidden="true">{number}</span>
  }
  return (
    <button
      type="button"
      style={style}
      aria-label={label}
      onClick={e => { e.stopPropagation(); onActivate() }}
    >
      {number}
    </button>
  )
}

export const HotspotStage = ({
  imageUrl, imageAlt, stageRef, overlay, children, onImageLoad,
  stageProps = {}, overlayProps = {},
}) => (
  <div ref={stageRef} {...stageProps} style={{ ...STAGE, ...stageProps.style }}>
    <img src={imageUrl} alt={imageAlt} style={IMAGE} draggable={false} onLoad={onImageLoad} />
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={OVERLAY} aria-hidden="true" {...overlayProps}>
      {overlay}
    </svg>
    {children}
  </div>
)
